/**
 * หน้าบูชา (?view=shop) — ตรรกะล้วน ไม่แตะ DOM/เครือข่าย (ทดสอบใน tests/shopLogic.test.ts)
 * ราคา/สต็อกมาจาก CF shopCatalog เท่านั้น · ตะกร้าฝั่งนี้ใช้แสดงผล ราคาจริงคำนวณใหม่ที่ memberApi.createShopCart
 * กติกาตะกร้าต้องตรง CF shop/shopCart.js: ประเภทเดียวต่อตะกร้า · สินค้า ≤3 บรรทัด · พิธี 1 บรรทัด
 */

export type ShopCategory = 'deity' | 'amulet' | 'ceremony'
export type ShopKind = 'product' | 'ceremony'

export interface ShopOption {
  key: string
  label: string
  price: number
  listPrice: number
  saving: number
  soldOut: boolean
  /** ISO — สร้างบารมีหมดเวลา (ไม่มี = ไม่จำกัด/ไม่มีสร้างบารมี) */
  promoEnd?: string
}

export interface ShopItem {
  id: string
  name: string
  category: ShopCategory
  kind: ShopKind
  images: string[]
  detail: string
  options: ShopOption[]
  defaultKey: string
  priceMin: number
  priceMax: number
  price: number
  listPrice: number
  saving: number
  soldOut: boolean
  /** พิธีเท่านั้น "YYYY-MM-DD" */
  eventDate?: string
  closeDate?: string
}

/** ธีมหน้าเลือกบูชา — ต้องตรงกับ CF shopLanding.SHOP_THEMES และแอดมิน shopLandingLogic.SHOP_THEMES */
export const SHOP_THEMES = ['royal', 'ivory', 'velvet', 'lotus', 'celestial'] as const
export type ShopTheme = (typeof SHOP_THEMES)[number]

export interface ShopLanding {
  heroEyebrow: string
  heroTitle: string
  heroSub: string
  heroImage: string
  primaryLabel: string
  secondaryLabel: string
  pinned: string[]
  live: { enabled: boolean; title: string; url: string }
  footerText: string
  theme: ShopTheme
}

/** CF ไม่ส่ง landing (รุ่นเก่า/อ่านไม่ได้) = ค่าเหล่านี้ — ตรงกับ DEFAULT_LANDING ฝั่ง CF */
export const DEFAULT_LANDING: ShopLanding = {
  heroEyebrow: 'ศรีคเนศ เทวาลัย',
  heroTitle: 'โปรดเลือกบูชา',
  heroSub: 'แจ้งยอดและชำระผ่าน LINE ได้ทันที',
  heroImage: '/shop-hero-deities.jpg',
  primaryLabel: 'เลือกบูชา',
  secondaryLabel: 'ร่วมพิธี',
  pinned: [],
  live: { enabled: false, title: '', url: '' },
  footerText: 'เลือก → แจ้งยอดใน LINE → จัดส่ง',
  theme: 'royal',
}

export function normalizeLanding(raw: Partial<ShopLanding> | null | undefined): ShopLanding {
  if (!raw || typeof raw !== 'object') return DEFAULT_LANDING
  const live = raw.live && typeof raw.live === 'object' ? raw.live : DEFAULT_LANDING.live
  return upgradeLegacyHero({
    ...DEFAULT_LANDING,
    ...raw,
    heroImage: /^https:\/\//.test(String(raw.heroImage || '')) ? String(raw.heroImage) : DEFAULT_LANDING.heroImage,
    pinned: Array.isArray(raw.pinned) ? raw.pinned.map(String) : [],
    theme: (SHOP_THEMES as readonly string[]).includes(String(raw.theme)) ? (raw.theme as ShopTheme) : 'royal',
    live: { enabled: !!live.enabled && /^https:\/\//.test(String(live.url || '')), title: String(live.title || ''), url: String(live.url || '') },
  })
}

/**
 * โหมดตัวอย่างของหน้าแอดมิน (หน้าบูชา › หน้าแรก ฝังหน้านี้ใน iframe): ?view=shop&preview=1[&theme=lotus]
 * ไม่บันทึกอะไร · ไม่ส่งสถิติ · ไม่เปิดคู่มือ · ไม่สร้างตะกร้า/ออร์เดอร์จริง · ธีมในลิงก์ชนะค่าที่บันทึก
 */
export function readShopPreview(search: string): { theme: ShopTheme | null } | null {
  try {
    const sp = new URLSearchParams(search)
    if (sp.get('preview') !== '1') return null
    const t = String(sp.get('theme') || '')
    return { theme: (SHOP_THEMES as readonly string[]).includes(t) ? (t as ShopTheme) : null }
  } catch {
    return null
  }
}

/** ต้นทางที่ส่งค่าร่างมาแสดงในโหมดตัวอย่างได้ (หน้าแอดมิน) */
export function isPreviewOrigin(origin: string): boolean {
  return /^https:\/\/(admin\.srikanett\.com|srikanett-order\.web\.app|srikanett-order\.firebaseapp\.com|srikanett-order--[a-z0-9-]+\.web\.app)$/.test(origin)
    || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin)
}

/** ข้อความจากหน้าแอดมิน { type:'srikanett-shop-preview', landing } → ค่าหน้าแรกที่ใช้แสดง (ผิดรูป = null ไม่เปลี่ยนอะไร) */
export function applyPreviewDraft(current: ShopLanding, data: unknown): ShopLanding | null {
  if (!data || typeof data !== 'object') return null
  const msg = data as { type?: unknown; landing?: unknown }
  if (msg.type !== 'srikanett-shop-preview' || !msg.landing || typeof msg.landing !== 'object') return null
  return normalizeLanding({ ...current, ...(msg.landing as Partial<ShopLanding>) })
}

/** รายการปักหมุดขึ้นก่อนตามลำดับที่แอดมินเลือก ที่เหลือคงลำดับเดิม */
export function sortPinned<T extends { id: string }>(items: T[], pinned: string[]): T[] {
  const rank = new Map(pinned.map((id, i) => [id, i]))
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => (rank.get(a.it.id) ?? 1e6 + a.i) - (rank.get(b.it.id) ?? 1e6 + b.i))
    .map((x) => x.it)
}

/** พิธีที่มีวันประกอบพิธี เรียงวันใกล้สุดก่อน (CF ตัดพิธีที่ปิดรับแล้วออกให้แล้ว) */
export function upcomingCeremonies(items: ShopItem[], limit = 2): ShopItem[] {
  return items
    .filter((i) => i.kind === 'ceremony' && i.eventDate)
    .sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate)))
    .slice(0, limit)
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
/** ISO → "31 ต.ค." ตามเวลาไทย */
export function thaiShortDate(iso: string): string {
  const t = Date.parse(iso || '')
  if (Number.isNaN(t)) return ''
  const d = new Date(t + 7 * 3600 * 1000)
  return `${d.getUTCDate()} ${TH_MONTHS[d.getUTCMonth()]}`
}

/** "2026-10-25" → { day: "25", month: "ต.ค." } */
export function thaiDayMonth(date: string): { day: string; month: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '')
  if (!m) return null
  return { day: String(Number(m[3])), month: TH_MONTHS[Number(m[2]) - 1] || '' }
}

export interface CartLine {
  id: string
  opt: string
  qty: number
}

export const MAX_CART_LINES = 3
export const MAX_QTY = 20

export const CATEGORY_TABS: Array<{ id: ShopCategory; label: string }> = [
  { id: 'deity', label: 'องค์เทพ' },
  { id: 'amulet', label: 'วัตถุมงคล' },
  { id: 'ceremony', label: 'พิธี' },
]

/** CF รุ่นแรกไม่มี options/category — เติมให้เป็นตัวเลือกเดียว หมวดองค์เทพ */
export function normalizeItem(raw: Partial<ShopItem> & { id: string; name: string; price: number }): ShopItem {
  const options = Array.isArray(raw.options) && raw.options.length
    ? raw.options
    : [{ key: '0', label: raw.name, price: raw.price, listPrice: raw.listPrice || 0, saving: raw.saving || 0, soldOut: !!raw.soldOut }]
  const category: ShopCategory = raw.category === 'amulet' || raw.category === 'ceremony' ? raw.category : 'deity'
  const prices = options.map((o) => o.price)
  return {
    id: raw.id,
    name: raw.name,
    category,
    kind: raw.kind === 'ceremony' || category === 'ceremony' ? 'ceremony' : 'product',
    images: Array.isArray(raw.images) ? raw.images : [],
    detail: String(raw.detail || ''),
    options,
    defaultKey: options.some((o) => o.key === raw.defaultKey) ? String(raw.defaultKey) : options[0].key,
    priceMin: Math.min(...prices),
    priceMax: Math.max(...prices),
    price: raw.price,
    listPrice: raw.listPrice || 0,
    saving: raw.saving || 0,
    soldOut: options.every((o) => o.soldOut),
    eventDate: typeof raw.eventDate === 'string' ? raw.eventDate : '',
    closeDate: typeof raw.closeDate === 'string' ? raw.closeDate : '',
  }
}

export interface ShopParams {
  item: string | null
  /** ตะกร้าที่ส่งต่อมาจากเบราว์เซอร์มือถือ → แอป LINE (?cart=) */
  cart: CartLine[]
  /** go=1 = ลูกค้ากดยืนยันบนเว็บแล้ว เด้งเข้าแอป LINE → สร้างออร์เดอร์ต่อเลย */
  go?: boolean
}

const CART_PARAM_MAX = 3

/** ตะกร้า → พารามิเตอร์ URL "id~opt~qty,id~opt~qty" (ส่งต่อเข้าแอป LINE ซึ่งไม่เห็น sessionStorage ของเบราว์เซอร์) */
export function encodeCartParam(cart: CartLine[]): string {
  return cart.slice(0, CART_PARAM_MAX).map((l) => `${l.id}~${l.opt}~${l.qty}`).join(',')
}

export function decodeCartParam(raw: string | null): CartLine[] {
  if (!raw) return []
  return raw.split(',').slice(0, 10).map((seg) => {
    const [id, opt, qty] = seg.split('~')
    return { id: String(id || ''), opt: String(opt || ''), qty: Math.floor(Number(qty)) }
  }).filter((l) => /^[A-Za-z0-9_-]{1,64}$/.test(l.id) && /^[0-9]{0,2}$/.test(l.opt) && l.qty >= 1 && l.qty <= MAX_QTY).slice(0, CART_PARAM_MAX)
}

/** วิธีเข้าสู่ระบบตอนชำระ — ใน LINE = พร้อม · มือถือนอก LINE = เปิดแอป LINE · คอมพิวเตอร์ = login ผ่านเว็บ */
export function checkoutRoute(ua: string, inLineClient: boolean): 'in_line' | 'open_line_app' | 'web_login' {
  if (inLineClient || /\bLine\//i.test(ua)) return 'in_line'
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) ? 'open_line_app' : 'web_login'
}

/** ?view=shop&item=<id> — อ่านทั้งที่อยู่ตรง ๆ และใน liff.state (เปิดผ่านลิงก์ LIFF) */
export function readShopParams(search: string, pathname = ''): ShopParams | null {
  try {
    const sp = new URLSearchParams(search)
    // ลิงก์สั้นสำหรับแชร์: /shop (มี meta ภาพ/หัวข้อของตัวเอง) — ไม่ต้องมี ?view=shop
    if (/^\/shop\/?$/.test(pathname)) {
      const item = (sp.get('item') || '').trim()
      return { item: item && /^[A-Za-z0-9_-]{1,64}$/.test(item) ? item : null, cart: decodeCartParam(sp.get('cart')), ...(sp.get('go') === '1' ? { go: true } : {}) }
    }
    let state = sp.get('liff.state')
    if (state && /%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state?.startsWith('?')) state = state.slice(1)
    const st = new URLSearchParams(state || '')
    const src = sp.get('view') === 'shop' ? sp : st.get('view') === 'shop' ? st : null
    if (!src) return null
    const item = (src.get('item') || '').trim()
    return { item: item && /^[A-Za-z0-9_-]{1,64}$/.test(item) ? item : null, cart: decodeCartParam(src.get('cart')), ...(src.get('go') === '1' ? { go: true } : {}) }
  } catch {
    return null
  }
}

export function formatBaht(n: number): string {
  return `฿${Math.round(Number(n) || 0).toLocaleString('en-US')}`
}

export function priceLabel(item: Pick<ShopItem, 'priceMin' | 'priceMax' | 'options'>): string {
  return item.options.length > 1 && item.priceMin !== item.priceMax ? `เริ่ม ${formatBaht(item.priceMin)}` : formatBaht(item.priceMin)
}

export function savingPercent(o: Pick<ShopOption, 'price' | 'listPrice'>): number {
  if (!(o.listPrice > o.price) || !(o.price > 0)) return 0
  return Math.round(((o.listPrice - o.price) / o.listPrice) * 100)
}

/** ตัวเลือกที่ควรเลือกไว้ตอนเปิดหน้า — ค่าเริ่มของแอดมิน ถ้าหมดใช้ตัวแรกที่ยังมี */
export function initialOptionKey(item: ShopItem): string {
  const def = item.options.find((o) => o.key === item.defaultKey && !o.soldOut)
  return (def || item.options.find((o) => !o.soldOut) || item.options[0]).key
}

export type CartError = 'max_lines' | 'sold_out' | 'mixed_kind' | 'ceremony_single'
export type CartResult = { cart: CartLine[]; error?: CartError }

export function cartKind(cart: CartLine[], items: ShopItem[]): ShopKind | null {
  const it = cart.length ? items.find((i) => i.id === cart[0].id) : undefined
  return it ? it.kind : null
}

export function addToCart(cart: CartLine[], items: ShopItem[], item: ShopItem, optKey: string, qty: number): CartResult {
  const opt = item.options.find((o) => o.key === optKey)
  if (!opt || opt.soldOut) return { cart, error: 'sold_out' }
  const kind = cartKind(cart, items)
  if (kind && kind !== item.kind) return { cart, error: 'mixed_kind' }
  const n = Math.max(1, Math.min(MAX_QTY, Math.floor(qty) || 1))
  const at = cart.findIndex((l) => l.id === item.id && l.opt === optKey)
  if (at >= 0) {
    const next = cart.slice()
    next[at] = { ...next[at], qty: Math.min(MAX_QTY, next[at].qty + n) }
    return { cart: next }
  }
  if (item.kind === 'ceremony' && cart.length >= 1) return { cart, error: 'ceremony_single' }
  if (cart.length >= MAX_CART_LINES) return { cart, error: 'max_lines' }
  return { cart: [...cart, { id: item.id, opt: optKey, qty: n }] }
}

export function setLineQty(cart: CartLine[], line: Pick<CartLine, 'id' | 'opt'>, qty: number): CartLine[] {
  const n = Math.floor(qty)
  const same = (l: CartLine) => l.id === line.id && l.opt === line.opt
  if (!(n >= 1)) return cart.filter((l) => !same(l))
  return cart.map((l) => (same(l) ? { ...l, qty: Math.min(MAX_QTY, n) } : l))
}

export function findOption(items: ShopItem[], line: Pick<CartLine, 'id' | 'opt'>): { item: ShopItem; option: ShopOption } | null {
  const item = items.find((i) => i.id === line.id)
  const option = item?.options.find((o) => o.key === line.opt)
  return item && option ? { item, option } : null
}

/** ตัดบรรทัดที่ไม่มีในแคตตาล็อก/หมดแล้ว · ตะกร้ารุ่นแรก (ไม่มี opt) ใช้ค่าเริ่มของรายการ */
export function reconcileCart(cart: Array<Partial<CartLine>>, items: ShopItem[]): CartLine[] {
  const out: CartLine[] = []
  for (const raw of cart) {
    const item = items.find((i) => i.id === raw.id)
    if (!item || !(Number(raw.qty) >= 1)) continue
    const opt = typeof raw.opt === 'string' && raw.opt ? raw.opt : item.defaultKey
    const f = item.options.find((o) => o.key === opt)
    if (!f || f.soldOut) continue
    out.push({ id: item.id, opt, qty: Math.min(MAX_QTY, Math.floor(Number(raw.qty))) })
  }
  return out
}

export function cartTotal(cart: CartLine[], items: ShopItem[]): number {
  return cart.reduce((sum, l) => sum + (findOption(items, l)?.option.price || 0) * l.qty, 0)
}

export function cartCount(cart: CartLine[]): number {
  return cart.reduce((n, l) => n + l.qty, 0)
}

export function cartMessage(code: string): string {
  return `บูชา #${code}`
}

export function cartErrorText(error: CartError): string {
  if (error === 'sold_out') return 'ตัวเลือกนี้หมดชั่วคราว'
  if (error === 'mixed_kind') return 'พิธีกับสินค้าต้องยืนยันแยกกันครั้งละประเภท'
  if (error === 'ceremony_single') return 'ร่วมพิธีได้ครั้งละ 1 รายการ'
  return `ใส่ตะกร้าได้ไม่เกิน ${MAX_CART_LINES} รายการต่อครั้ง`
}

/* ---------- วิธีสั่งบูชา (motion guide) — แสดงอัตโนมัติไม่เกิน 3 ครั้งต่อบัญชี (นับในเครื่องแยกตาม LINE · ไม่ login = ต่อเครื่อง) ---------- */
export const GUIDE_MAX_AUTO = 3

export interface GuideState { count: number; never: boolean }

export function guideStorageKey(userId: string | null | undefined): string {
  const id = String(userId || '').trim()
  return `oc-shop-guide:${/^U[0-9a-f]{32}$/i.test(id) ? id : 'device'}`
}

export function parseGuideState(raw: string | null): GuideState {
  try {
    const d = JSON.parse(raw || '{}') as Partial<GuideState>
    return { count: Math.max(0, Math.floor(Number(d.count) || 0)), never: d.never === true }
  } catch {
    return { count: 0, never: false }
  }
}

export function shouldAutoShowGuide(s: GuideState): boolean {
  return !s.never && s.count < GUIDE_MAX_AUTO
}

/** ข้อความเริ่มต้นเดิม (ก่อน 18 ต.ค. 2569) — เจอในค่าที่แอดมินบันทึกไว้ = ใช้ข้อความใหม่แทน */
const LEGACY_HERO: Record<string, string> = {
  'องค์พ่อพระพิฆเนศ · พระแม่ลักษมี': 'heroEyebrow',
  'บูชาองค์เทพ\nรับยอดในแชท LINE': 'heroTitle',
  'เลือกองค์ ใส่ตะกร้า แล้วระบบแจ้งยอดและ QR ชำระเงินในแชททันที': 'heroSub',
}
export function upgradeLegacyHero(l: ShopLanding): ShopLanding {
  const out = { ...l }
  for (const [text, field] of Object.entries(LEGACY_HERO)) {
    const k = field as 'heroEyebrow' | 'heroTitle' | 'heroSub'
    if (out[k] === text) out[k] = DEFAULT_LANDING[k]
  }
  return out
}

/** แยกคำท้ายหัวข้อ "บูชา" ไว้ทำสีทอง · ไม่มีคำนี้ = ทั้งบรรทัดสีเดียว */
export function splitHeroTitle(title: string): { lead: string; accent: string } {
  const m = /^(.*?)(บูชา)$/s.exec(String(title || ''))
  return m ? { lead: m[1], accent: m[2] } : { lead: String(title || ''), accent: '' }
}
